/* ============================================================================
   McDuffy Virtual Care — the dog's always-on care helper (never a vet).
   Chat (grounded) + "here's what I noticed" cards + photo scan (docs & body) +
   a warm vet-ready handoff. Reads the signed-in email from sessionStorage; talks
   to /api/ask, /api/vision/extract, /api/care/{insights,vet-summary,body-scan},
   /api/dog-progress, /api/dog-profiles. Nutrition & husbandry only — the backend
   enforces the never-diagnose guardrails.
   ============================================================================ */
(function () {
  'use strict';
  var root = document.querySelector('[data-vc]');
  if (!root) return;
  var BACKEND = 'https://mcduffy-backend-production.up.railway.app';
  var email = '';
  var booted = false;
  var identityGeneration = 0;
  var activeRequestControllers = [];
  var activeIntervals = [];
  var activeToasts = [];
  var greetTimer = null;
  function readEmail() { try { return (sessionStorage.getItem('mcduffy_account_email') || '').trim().toLowerCase(); } catch (e) { return ''; } }
  /* Deferred boot: this script loads before a FRESH login writes the email to
     sessionStorage, so a hard bail here left Virtual Care invisible until reload —
     the flagship no-showing at the first impression. The account page dispatches
     'mcduffy:login' after sign-in; init() is idempotent. */
  function init() {
    var e = readEmail();
    if (!e || e.indexOf('@') < 0) { if (booted) resetVirtualCare(); return; }
    if (booted && e === email) return;
    resetVirtualCare();
    email = e;
    booted = true;
    boot();
  }

  function freshState() { return { dog: 'your dog', photo: '', vet: null, vaccines: [], sex: '', history: [], busy: false, consented: false }; }
  var S = freshState();
  function captureIdentity() { return { email: email, generation: identityGeneration }; }
  function isCurrentIdentity(identity) { return !!identity && !!identity.email && identity.email === email && identity.generation === identityGeneration && identity.email === readEmail(); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  /* possessive pronoun from the profile's dog_sex — "her" was hardcoded for every dog */
  function pos() { return /^(m|boy)/i.test(S.sex) ? 'his' : /^(f|girl)/i.test(S.sex) ? 'her' : 'their'; }
  /* per-CALL timeout (never global: vision/body-scan legitimately run 15-40s;
     a blanket abort would break the scan flows). A hung fetch on PH mobile
     networks used to leave the chat spinner up forever. */
  function api(path, opts, timeoutMs) {
    opts = opts || {};
    var timer = null;
    var ctrl = null;
    try {
      if (typeof AbortController !== 'undefined') {
        ctrl = new AbortController(); opts.signal = ctrl.signal;
        activeRequestControllers.push(ctrl);
        if (timeoutMs) timer = setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, timeoutMs);
      }
    } catch (e) {}
    function cleanup() {
      if (timer) clearTimeout(timer);
      if (ctrl) {
        var ix = activeRequestControllers.indexOf(ctrl);
        if (ix > -1) activeRequestControllers.splice(ix, 1);
      }
    }
    return fetch(BACKEND + path, opts).then(function (r) {
      cleanup();
      return r.json().catch(function () { return { ok: false }; });
    }, function (err) { cleanup(); throw err; });
  }
  function post(path, body, timeoutMs) { return api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, timeoutMs); }

  var I = {
    paw: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="10" r="2"/><circle cx="10.5" cy="7" r="2"/><circle cx="15.5" cy="7" r="2"/><circle cx="19" cy="10.5" r="1.8"/><path d="M12 12c-2.6 0-5 2-5 4.3 0 1.7 1.4 2.7 3 2.7.9 0 1.4-.4 2-.4s1.1.4 2 .4c1.6 0 3-1 3-2.7C17 14 14.6 12 12 12z"/></svg>',
    send: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
  };

  /* ---------- mount shell ---------- */
  root.innerHTML =
    '<div class="vc__hidden" data-vc-inner>' +
      '<div class="vc-hero"><div class="vc-hero__ava" data-vc-ava>' + I.paw + '</div>' +
        '<div><div class="vc-hero__t" data-vc-hero-t>McDuffy Virtual Care</div><div class="vc-hero__s" data-vc-hero-s>Your dog’s food &amp; care helper.</div></div></div>' +
      /* audit 2026-07-05: "Feeding" (a chat macro duplicating two surfaces on the
         same tab) and "Find a vet" (an emergency-Maps link cosplaying as a
         directory) were killed; the refer card keeps the honest emergency
         actions. "Weight check" renamed — it opens the body-condition scan. */
      /* 2026-07-08 (owner): shrunk to an ENTRY-ONLY card. The always-open chat
         thread, "Scan a photo" and "Body scan" were dropped from the fold — the
         chat is hidden until "Ask McDuffy" is tapped (see the ask handler). The
         scan/openScan code stays available for in-chat CTAs. */
      '<div class="vc-actions">' +
        '<button class="vc-act" data-vc-act="ask"><span class="vc-act__ic">💬</span> Ask McDuffy</button>' +
      '</div>' +
      '<div class="vc-noticed" data-vc-noticed hidden></div>' +
      '<div class="vc-chat" data-vc-chatwrap hidden>' +
        '<div class="vc-chat__head"><div class="vc-chat__ava">' + I.paw + '</div>' +
          '<div><div class="vc-chat__name">McDuffy</div><div class="vc-chat__sub" data-vc-sub>your dog’s helper</div></div></div>' +
        '<div class="vc-thread" data-vc-thread></div>' +
        '<div class="vc-chips" data-vc-chips></div>' +
        '<form class="vc-form" data-vc-form><input class="vc-input" data-vc-input placeholder="Ask about feeding, portions…" autocomplete="off" maxlength="400">' +
          '<button class="vc-send" type="submit" data-vc-send>' + I.send + '</button></form>' +
        '<p class="vc-disc">McDuffy gives feeding &amp; general care guidance, not veterinary diagnosis. For medical concerns, see your vet.</p>' +
      '</div>' +
    '</div>';

  var inner = root.querySelector('[data-vc-inner]');
  var thread = root.querySelector('[data-vc-thread]');
  var chipsEl = root.querySelector('[data-vc-chips]');
  var form = root.querySelector('[data-vc-form]');
  var input = root.querySelector('[data-vc-input]');
  var sendBtn = root.querySelector('[data-vc-send]');
  var noticedEl = root.querySelector('[data-vc-noticed]');

  function resetVirtualCare() {
    identityGeneration += 1;
    activeRequestControllers.splice(0).forEach(function (ctrl) { try { ctrl.abort(); } catch (e) {} });
    activeIntervals.splice(0).forEach(function (timer) { clearInterval(timer); });
    if (greetTimer) { clearTimeout(greetTimer); greetTimer = null; }
    activeToasts.splice(0).forEach(function (node) { if (node && node.parentNode) node.parentNode.removeChild(node); });
    if (sheet) {
      if (sheet.parentNode) sheet.parentNode.removeChild(sheet);
      sheet = null;
      document.body.style.overflow = '';
    }
    if (consentEl && consentEl.parentNode) consentEl.parentNode.removeChild(consentEl);
    consentEl = null;
    greeted = false;
    greetEl = null;
    S = freshState();
    email = '';
    booted = false;

    thread.innerHTML = '';
    chipsEl.innerHTML = '';
    noticedEl.innerHTML = '';
    noticedEl.hidden = true;
    var chatWrap = root.querySelector('[data-vc-chatwrap]');
    if (chatWrap) chatWrap.hidden = true;
    input.value = '';
    sendBtn.disabled = false;
    root.querySelector('[data-vc-hero-t]').textContent = 'McDuffy Virtual Care';
    root.querySelector('[data-vc-hero-s]').textContent = 'Your dog’s food & care helper.';
    root.querySelector('[data-vc-sub]').textContent = 'your dog’s helper';
    root.querySelector('[data-vc-ava]').innerHTML = I.paw;
    inner.classList.add('vc__hidden');
  }

  function setDog(name) {
    S.dog = name;
    root.querySelector('[data-vc-hero-t]').textContent = name + '’s care';
    root.querySelector('[data-vc-sub]').textContent = name + '’s food & care helper';
    /* if the 2500ms fallback greeted with "your dog" before the profile arrived,
       refresh it in place so the first message still carries the dog's name */
    if (greetEl && S.history.length === 0) greetEl.textContent = greetText();
    renderChips();
  }
  function setAvatar(url) { if (url) root.querySelector('[data-vc-ava]').innerHTML = '<img alt="" src="' + esc(url) + '">'; }
  function setHeroSub(t) { root.querySelector('[data-vc-hero-s]').textContent = t; }

  function renderChips() {
    var qs = ['How much should ' + S.dog + ' eat?', 'Switching from old food', 'Is chicken okay for ' + S.dog + '?', 'Good treats for ' + S.dog + '?'];
    chipsEl.innerHTML = qs.map(function (q) { return '<button type="button" class="vc-chip">' + esc(q) + '</button>'; }).join('');
  }
  chipsEl.addEventListener('click', function (e) { var b = e.target.closest('.vc-chip'); if (b && !S.busy) ask(b.textContent); });

  function addMsg(text, who) { var d = document.createElement('div'); d.className = 'vc-msg vc-msg--' + who; d.textContent = text; thread.appendChild(d); d.scrollIntoView({ block: 'nearest' }); return d; }
  function typing() { var d = document.createElement('div'); d.className = 'vc-typing'; d.innerHTML = '<i></i><i></i><i></i>'; thread.appendChild(d); d.scrollIntoView({ block: 'nearest' }); return d; }

  /* ---------- AI/health-data consent (RA 10173) — asked once, then remembered ---------- */
  var consentEl = null;
  function ensureConsent(then) {
    if (S.consented) { then(); return; }
    if (consentEl) return; /* already showing */
    var consentIdentity = captureIdentity();
    consentEl = document.createElement('div');
    consentEl.className = 'vc-consent';
    consentEl.innerHTML =
      '<div class="vc-consent__t">Before McDuffy helps</div>' +
      '<p class="vc-consent__b">To answer about ' + esc(S.dog) + ', McDuffy uses ' + esc(S.dog) + '’s record — diet, weight, allergies, vaccines — and reads any document you choose to scan. It gives feeding &amp; general care guidance, not veterinary diagnosis, and your data is never sold. You can withdraw anytime by messaging us.</p>' +
      '<button type="button" class="vc-consent__ok" data-consent-ok>I agree — let’s go</button>';
    thread.appendChild(consentEl); consentEl.scrollIntoView({ block: 'nearest' });
    consentEl.querySelector('[data-consent-ok]').addEventListener('click', function () {
      if (!isCurrentIdentity(consentIdentity)) return;
      S.consented = true;
      post('/api/dog-profiles', { email: consentIdentity.email, consent: { ai_care: true, ai_docs: true } }).catch(function () {});
      if (consentEl) { consentEl.parentNode.removeChild(consentEl); consentEl = null; }
      then();
    });
  }

  function referCard(text, urgent) {
    var v = S.vet || {};
    var acts = '';
    if (v.phone) acts += '<a class="vc-refer__act vc-refer__act--primary" href="tel:' + esc(String(v.phone).replace(/[^0-9+]/g, '')) + '">📞 Call ' + esc(v.clinic || 'your vet') + '</a>';
    acts += '<a class="vc-refer__act" target="_blank" rel="noopener" href="https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('24 hour emergency veterinary clinic near me') + '">🗺️ Nearest 24h vet</a>';
    acts += '<button class="vc-refer__act" data-vc-summary>📋 Copy ' + esc(S.dog) + '’s summary for the vet</button>';
    var d = document.createElement('div');
    d.className = 'vc-refer' + (urgent ? ' vc-refer--urgent' : '');
    d.innerHTML = '<div class="vc-refer__t">' + (urgent ? 'Please see a vet soon' : 'This one’s for a vet') + '</div>' +
      '<div class="vc-refer__b">' + esc(text) + '</div><div class="vc-refer__acts">' + acts + '</div>';
    thread.appendChild(d); d.scrollIntoView({ block: 'nearest' });
  }

  function ask(q) {
    q = String(q || '').trim(); if (!q || S.busy) return;
    if (!S.consented) { ensureConsent(function () { ask(q); }); return; }
    var requestIdentity = captureIdentity();
    if (!isCurrentIdentity(requestIdentity)) return;
    S.busy = true; sendBtn.disabled = true; input.value = '';
    addMsg(q, 'you');
    var t = typing();
    post('/api/ask', { email: requestIdentity.email, question: q, history: S.history.slice(-6) }, 25000)
      .then(function (j) {
        if (!isCurrentIdentity(requestIdentity)) return;
        if (t.parentNode) t.parentNode.removeChild(t);
        if (j && j.ok && j.answer) {
          if (j.refer) { referCard(j.answer, j.refer === 'urgent'); }
          else { addMsg(j.answer, 'ai'); }
          S.history.push({ role: 'user', content: q }); S.history.push({ role: 'assistant', content: j.answer });
        } else {
          addMsg((j && j.error) || 'McDuffy couldn’t answer just now — message us on Viber.', 'ai');
          if (j && j.setup_url) { /* no profile yet → give a real path, not a dead-end sentence */
            var cta = document.createElement('a');
            cta.className = 'vc-msg vc-msg--ai vc-msg--cta';
            cta.href = j.setup_url;
            cta.textContent = 'Finish ' + S.dog + '’s page →';
            thread.appendChild(cta); cta.scrollIntoView({ block: 'nearest' });
          }
        }
      })
      .catch(function () { if (!isCurrentIdentity(requestIdentity)) return; if (t.parentNode) t.parentNode.removeChild(t); addMsg('Couldn’t reach McDuffy — check your connection or message us on Viber.', 'ai'); })
      .then(function () { if (!isCurrentIdentity(requestIdentity)) return; S.busy = false; sendBtn.disabled = false; input.focus(); });
  }
  form.addEventListener('submit', function (e) { e.preventDefault(); ask(input.value); });

  /* copy vet summary (from a refer card) */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-vc-summary]')) { e.preventDefault(); copySummary(); }
  });
  function copySummary() {
    var requestIdentity = captureIdentity();
    if (!isCurrentIdentity(requestIdentity)) return;
    post('/api/care/vet-summary', { email: requestIdentity.email }).then(function (j) {
      if (!isCurrentIdentity(requestIdentity)) return;
      if (!(j && j.ok && j.summary_text)) { toast('Couldn’t build the summary just now — try again in a moment.'); return; }
      var txt = j.summary_text;
      try { navigator.clipboard.writeText(txt).then(function () { if (isCurrentIdentity(requestIdentity)) toast('Copied ' + S.dog + '’s summary — paste it for your vet.'); }, function () { if (isCurrentIdentity(requestIdentity)) fallbackCopy(txt); }); }
      catch (err) { if (isCurrentIdentity(requestIdentity)) fallbackCopy(txt); }
    }).catch(function () { if (isCurrentIdentity(requestIdentity)) toast('Couldn’t build the summary just now — try again in a moment.'); });
  }
  /* window.open from an async callback gets popup-blocked on iOS Safari and
     silently no-ops — a textarea + execCommand copy works in the same context */
  function fallbackCopy(txt) {
    var ta = document.createElement('textarea');
    ta.value = txt; ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(ta); ta.select();
    var ok = false; try { ok = document.execCommand('copy'); } catch (e) {}
    ta.remove();
    toast(ok ? 'Copied ' + S.dog + '’s summary — paste it for your vet.' : 'Couldn’t copy automatically — long-press the chat to copy.');
  }
  function toast(msg) { var d = document.createElement('div'); d.textContent = msg; d.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);background:#0F356F;color:#fff;padding:12px 18px;border-radius:12px;font:600 14px system-ui;z-index:100001;max-width:90vw;text-align:center'; document.body.appendChild(d); activeToasts.push(d); setTimeout(function () { var ix = activeToasts.indexOf(d); if (ix > -1) activeToasts.splice(ix, 1); d.remove(); }, 3200); }

  /* ---------- quick actions ---------- */
  root.addEventListener('click', function (e) {
    var a = e.target.closest('[data-vc-act]'); if (!a) return;
    var k = a.getAttribute('data-vc-act');
    if (k === 'ask') {
      var cw = root.querySelector('[data-vc-chatwrap]'); if (cw) cw.hidden = false;
      input.focus(); input.scrollIntoView({ block: 'center' });
    }
    else if (k === 'scan') { openScan(); }
    else if (k === 'weight') { openScan('body'); }
  });

  /* ---------- photo scan sheet (Frame → Analyze → Result) ---------- */
  var sheet = null;
  function closeSheet() { if (!sheet) return; var ov = sheet; sheet = null; ov.classList.remove('is-in'); document.body.style.overflow = ''; setTimeout(function () { if (ov.parentNode) ov.parentNode.removeChild(ov); }, 240); }
  function openScan(preKind) {
    if (!S.consented) { ensureConsent(function () { openScan(preKind); }); return; }
    closeSheet();
    var ov = document.createElement('div'); ov.className = 'vc-ov';
    ov.innerHTML = '<div class="vc-ov__scrim" data-close></div><div class="vc-sheet"><div class="vc-sheet__grip"></div><div data-stage></div></div>';
    document.body.appendChild(ov); document.body.style.overflow = 'hidden'; sheet = ov;
    setTimeout(function () { ov.classList.add('is-in'); }, 20);
    ov.addEventListener('click', function (e) { if (e.target.closest('[data-close]')) closeSheet(); });
    var stage = ov.querySelector('[data-stage]');
    if (preKind) startCapture(stage, preKind); else renderKinds(stage);
  }
  /* only what the renderer actually saves (vaccines + clinic) — the old "any pet
     document · prescription, lab, food label…" kind extracted those fields then
     silently threw them away, an advertised lie (audit 2026-07-05) */
  var KINDS = [
    { k: 'vaccine', ic: '💉', t: 'Vaccine card', s: 'Read the shots & dates into ' + '{dog}' + '’s record' },
    { k: 'vet', ic: '🧾', t: 'Vet receipt / record', s: 'Pull the clinic & details' },
    { k: 'body', ic: '⚖️', t: 'Body scan', s: 'See ' + '{dog}' + '’s shape for feeding' }
  ];
  function renderKinds(stage) {
    stage.innerHTML = '<h2 class="vc-sheet__h">Scan a photo</h2><p class="vc-sheet__p">What are you snapping? I’ll read it for you.</p>' +
      '<div class="vc-kinds">' + KINDS.map(function (x) { return '<button class="vc-kind" data-kind="' + x.k + '"><span class="vc-kind__ic">' + x.ic + '</span><span>' + esc(x.t) + '<span class="vc-kind__s">' + esc(x.s.replace('{dog}', S.dog)) + '</span></span></button>'; }).join('') + '</div>';
    stage.addEventListener('click', function (e) { var b = e.target.closest('[data-kind]'); if (b) startCapture(stage, b.getAttribute('data-kind')); });
  }
  function startCapture(stage, kind) {
    var isBody = kind === 'body';
    stage.innerHTML = '<h2 class="vc-sheet__h">' + (isBody ? 'Body scan' : 'Scan a photo') + '</h2>' +
      '<p class="vc-sheet__p">' + (isBody ? 'Take a photo of ' + esc(S.dog) + ' standing, from the side — good light, whole body in frame.' : 'Fit the whole card/page in frame · hold ~20 cm away · good light.') + '</p>' +
      '<button class="vc-cta" data-pick>📷 Take / choose photo</button>' +
      '<input type="file" accept="image/*" capture="environment" data-file hidden>' +
      '<button class="vc-cta vc-cta--ghost" data-close>Cancel</button>';
    var file = stage.querySelector('[data-file]');
    stage.querySelector('[data-pick]').addEventListener('click', function () { file.click(); });
    file.addEventListener('change', function () {
      var f = file.files && file.files[0];
      var requestIdentity = captureIdentity();
      if (f) resizeThen(f, requestIdentity, function (dataUrl) {
        if (isCurrentIdentity(requestIdentity)) analyze(stage, kind, dataUrl, requestIdentity);
      });
    });
  }
  function resizeThen(f, requestIdentity, cb) {
    var rd = new FileReader();
    rd.onload = function () { if (!isCurrentIdentity(requestIdentity)) return; var img = new Image(); img.onload = function () {
      if (!isCurrentIdentity(requestIdentity)) return;
      var MAX = 1500, scale = Math.min(1, MAX / Math.max(img.width, img.height));
      var c = document.createElement('canvas'); c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); cb(c.toDataURL('image/jpeg', 0.82));
    }; img.onerror = function () { if (isCurrentIdentity(requestIdentity)) cb(null); }; img.src = rd.result; };
    rd.readAsDataURL(f);
  }
  function analyze(stage, kind, dataUrl, requestIdentity) {
    requestIdentity = requestIdentity || captureIdentity();
    if (!isCurrentIdentity(requestIdentity)) return;
    if (!dataUrl) { stage.innerHTML = '<p class="vc-sheet__p">Couldn’t open that image. <button class="vc-cta vc-cta--ghost" data-close>Close</button></p>'; return; }
    var steps = kind === 'body' ? ['Looking at ' + S.dog + '…', 'Checking ' + pos() + ' build…', 'Almost there…'] : ['Reading the details…', 'Matching to ' + S.dog + '’s record…', 'Almost there…'];
    stage.innerHTML = '<div class="vc-analyze"><img alt="" src="' + dataUrl + '"><div class="vc-analyze__spin"></div><div class="vc-analyze__step" data-step>' + esc(steps[0]) + '</div></div>';
    var stepEl = stage.querySelector('[data-step]'); var si = 0;
    var iv = setInterval(function () { si = (si + 1) % steps.length; stepEl.textContent = steps[si]; }, 1600);
    activeIntervals.push(iv);
    var done = function () { clearInterval(iv); var ix = activeIntervals.indexOf(iv); if (ix > -1) activeIntervals.splice(ix, 1); };
    if (kind === 'body') {
      post('/api/care/body-scan', { email: requestIdentity.email, image: dataUrl }, 60000).then(function (j) { done(); if (isCurrentIdentity(requestIdentity)) bodyResult(stage, j); }).catch(function () { done(); if (isCurrentIdentity(requestIdentity)) scanError(stage); });
    } else {
      post('/api/vision/extract', { image: dataUrl, kind: kind }, 60000).then(function (j) { done(); if (isCurrentIdentity(requestIdentity)) docResult(stage, kind, j); }).catch(function () { done(); if (isCurrentIdentity(requestIdentity)) scanError(stage); });
    }
  }
  function scanError(stage) { stage.innerHTML = '<p class="vc-sheet__p">Couldn’t read that photo just now. Try again in good light, or enter it by hand.</p><button class="vc-cta vc-cta--ghost" data-close>Close</button>'; }

  function docResult(stage, kind, j) {
    if (!(j && j.ok && j.data)) return scanError(stage);
    var d = j.data;
    if (j.confidence === 'low') { stage.innerHTML = '<h2 class="vc-sheet__h">A bit blurry</h2><p class="vc-sheet__p">Mind retaking it in better light? Clear photos read best.</p><button class="vc-cta" data-retake>Retake</button><button class="vc-cta vc-cta--ghost" data-close>Cancel</button>'; stage.querySelector('[data-retake]').addEventListener('click', function () { startCapture(stage, kind); }); return; }
    var vax = (d.vaccines || []).filter(function (v) { return v && v.name; });
    var rows = '';
    if (vax.length) rows += '<div style="font-weight:700;margin:6px 0 4px">Vaccines I read:</div>' + vax.map(function (v, i) { return '<div class="vc-result__field"><input data-vx-name data-vx-given="' + esc(v.date || '') + '" value="' + esc(v.name) + '"><input type="date" data-vx-date value="' + esc(v.due_date || '') + '"></div>'; }).join('');
    if (d.clinic_name) rows += '<div class="vc-result__field"><span>Clinic</span><input data-clinic value="' + esc(d.clinic_name) + '"></div>';
    /* extraction SUCCEEDED but found nothing saveable → say so honestly (the old
       path claimed "couldn't read that photo", blaming the user's photo) */
    if (!rows) { stage.innerHTML = '<h2 class="vc-sheet__h">Read it — nothing to save</h2><p class="vc-sheet__p">I could read the photo, but didn’t find vaccine shots or a clinic on it. Try ' + esc(S.dog) + '’s vaccine card, or add shots by hand on ' + esc(S.dog) + '’s page.</p><button class="vc-cta vc-cta--ghost" data-close>Close</button>'; return; }
    stage.innerHTML = '<h2 class="vc-sheet__h">Here’s what I read</h2><p class="vc-sheet__p">Check it, then save to ' + esc(S.dog) + '’s record.</p>' + rows +
      '<button class="vc-cta vc-cta--neon" data-save>Save to ' + esc(S.dog) + '’s record</button><button class="vc-cta vc-cta--ghost" data-close>Not now</button>';
    stage.querySelector('[data-save]').addEventListener('click', function () {
      var requestIdentity = captureIdentity();
      if (!isCurrentIdentity(requestIdentity)) return;
      var scanned = [].map.call(stage.querySelectorAll('[data-vx-name]'), function (n, i) {
        var dateEl = stage.querySelectorAll('[data-vx-date]')[i];
        return { name: n.value.trim(), due_date: (dateEl && dateEl.value) || '', given_date: n.getAttribute('data-vx-given') || '' };
      }).filter(function (v) { return v.name; });
      var clinicEl = stage.querySelector('[data-clinic]');
      var patch = { email: requestIdentity.email };
      if (scanned.length) {
        /* MERGE into the stored list — the profile endpoint REPLACES the vaccines
           array, so sending only this card's rows used to wipe every previously
           saved shot (audit 2026-07-05). Scanned rows win on a name match. */
        var merged = (S.vaccines || []).slice();
        scanned.forEach(function (nv) {
          var ix = -1;
          for (var m = 0; m < merged.length; m++) { if ((merged[m].name || '').toLowerCase() === nv.name.toLowerCase()) { ix = m; break; } }
          if (ix > -1) merged[ix] = { name: nv.name, due_date: nv.due_date || merged[ix].due_date || '', given_date: nv.given_date || merged[ix].given_date || '' };
          else merged.push(nv);
        });
        patch.vaccines = merged;
      }
      /* field-preserving vet patch — a bare {clinic} used to blank the stored
         phone/name/address because the endpoint rebuilds the whole vet object */
      if (clinicEl && clinicEl.value.trim()) patch.vet = Object.assign({}, S.vet || {}, { clinic: clinicEl.value.trim() });
      post('/api/dog-profiles', patch).then(function () {
        if (!isCurrentIdentity(requestIdentity)) return;
        if (patch.vaccines) S.vaccines = patch.vaccines;
        if (patch.vet) S.vet = patch.vet;
        closeSheet(); toast('Saved to ' + S.dog + '’s record ✓'); loadInsights();
      }).catch(function () {});
    });
  }
  function bodyResult(stage, j) {
    if (!(j && j.ok)) return scanError(stage);
    if (j.refer) { stage.innerHTML = '<h2 class="vc-sheet__h" style="color:var(--vc-red)">Let’s have a vet look</h2><div class="vc-obs">' + esc(j.observation) + '</div>' + (S.vet && S.vet.phone ? '<a class="vc-cta" href="tel:' + esc(String(S.vet.phone).replace(/[^0-9+]/g, '')) + '">📞 Call ' + esc(S.vet.clinic || 'your vet') + '</a>' : '') + '<button class="vc-cta vc-cta--ghost" data-close>Close</button>'; return; }
    /* low-confidence read → retake, never save a guess (docs already did this; body didn't) */
    if (j.confidence === 'low') { stage.innerHTML = '<h2 class="vc-sheet__h">A bit hard to see</h2><p class="vc-sheet__p">Mind retaking it? Side-on, whole body in frame, good light reads best.</p><button class="vc-cta" data-retake>Retake</button><button class="vc-cta vc-cta--ghost" data-close>Cancel</button>'; stage.querySelector('[data-retake]').addEventListener('click', function () { startCapture(stage, 'body'); }); return; }
    var rep = j.reportion;
    var html = '<h2 class="vc-sheet__h">' + esc(S.dog) + '’s build</h2><div class="vc-obs">' + esc(j.observation || 'Looks good!') + '</div>';
    if (rep) html += '<p class="vc-sheet__p">Based on that, I’d ' + (rep.direction === 'up' ? 'nudge ' + pos() + ' food up' : 'ease ' + pos() + ' food down') + ' a little.</p><button class="vc-cta vc-cta--neon" data-reportion>Update to ' + rep.suggested_grams + ' g/day</button>';
    html += '<button class="vc-cta vc-cta--ghost" data-close>' + (rep ? 'Keep current portion' : 'Done') + '</button>';
    stage.innerHTML = html;
    if (rep) stage.querySelector('[data-reportion]').addEventListener('click', function () {
      var requestIdentity = captureIdentity();
      if (!isCurrentIdentity(requestIdentity)) return;
      post('/api/dog-profiles', { email: requestIdentity.email, daily_grams: rep.suggested_grams, bags_per_month: rep.bags_per_month || undefined }).then(function () {
        if (!isCurrentIdentity(requestIdentity)) return;
        closeSheet(); toast('Portion updated to ' + rep.suggested_grams + ' g/day ✓'); loadInsights();
      }).catch(function () {});
    });
  }

  /* ---------- "here's what I noticed" ---------- */
  function loadInsights(requestIdentity) {
    var identity = requestIdentity || captureIdentity();
    if (!isCurrentIdentity(identity)) return;
    api('/api/care/insights?email=' + encodeURIComponent(identity.email)).then(function (j) {
      if (!isCurrentIdentity(identity)) return;
      if (!(j && j.ok && j.cards && j.cards.length)) { noticedEl.innerHTML = ''; noticedEl.hidden = true; return; }
      noticedEl.innerHTML = j.cards.map(function (c) {
        var cls = c.tone === 'celebrate' ? ' vc-note--celebrate' : c.tone === 'attention' ? ' vc-note--attention' : '';
        var body = '<div class="vc-note__t">' + esc(c.title) + '</div>' + (c.sub ? '<div class="vc-note__t" style="font-weight:500;color:var(--vc-olive-2);font-size:12.5px;margin-top:3px">' + esc(c.sub) + '</div>' : '');
        if (c.action && c.action.tel) body += '<a class="vc-note__act" href="tel:' + esc(c.action.tel) + '">📞 ' + esc(c.action.label) + '</a>';
        if (c.items) body += '<div class="vc-note__spark">' + c.items.map(function (it) { return '<button class="vc-note__chip" data-vc-goto="' + esc(it.step || '') + '">' + esc(it.label) + '</button>'; }).join('') + '</div>';
        var key = c.type === 'vaccine_due' ? 'Vaccine' : c.type === 'portion' ? 'Her plan' : c.type === 'birthday' ? 'Birthday' : c.type === 'complete' ? 'Finish her page' : 'Note';
        return '<div class="vc-note' + cls + '"><div class="vc-note__k">' + key + '</div>' + body + '</div>';
      }).join('');
      noticedEl.hidden = false;
    }).catch(function () {});
  }
  noticedEl.addEventListener('click', function (e) { var b = e.target.closest('[data-vc-goto]'); if (b) { var step = b.getAttribute('data-vc-goto'); location.href = '/pages/member-setup'; } });

  /* ---------- boot: load everything, THEN greet (so the name is known) ---------- */
  var greeted = false, greetEl = null;
  function greetText() { return 'Hi! I’m McDuffy — I help with ' + S.dog + '’s food, feeding, and everyday care. I’m not a vet, so if anything looks off with ' + S.dog + '’s health, I’ll always point you to a real one. What can I help with? 🐶'; }
  function greet() {
    if (greeted) return; greeted = true;
    greetEl = addMsg(greetText(), 'ai');
  }
  function refreshIdentity(requestIdentity) {
    var identity = requestIdentity || captureIdentity();
    if (!isCurrentIdentity(identity)) return Promise.resolve();
    return api('/api/dog-profiles?email=' + encodeURIComponent(identity.email)).then(function (p) {
      if (!isCurrentIdentity(identity)) return;
      if (p && p.dog_name) setDog(p.dog_name);
      if (p && p.photo_url) setAvatar(p.photo_url);
      if (p && p.vet) S.vet = p.vet;
      if (p && p.dog_sex) S.sex = p.dog_sex;
      if (p && Array.isArray(p.vaccines)) S.vaccines = p.vaccines; /* held for scan-save merges */
      /* OR-guard: never clobber consent the user just granted this session while
         this (possibly slow) GET was still in flight — only ever set true, so the
         consent gate isn't re-shown after they already agreed. */
      S.consented = S.consented || !!(p && p.consent && p.consent.ai_care); /* asked once, then remembered */
    });
  }
  function boot() {
    var identity = captureIdentity();
    if (!isCurrentIdentity(identity)) return;
    inner.classList.remove('vc__hidden');
    refreshIdentity(identity).then(function () { if (isCurrentIdentity(identity)) greet(); }).catch(function () { if (isCurrentIdentity(identity)) greet(); });
    api('/api/dog-progress?email=' + encodeURIComponent(identity.email)).then(function (j) {
      if (!isCurrentIdentity(identity)) return;
      if (j && j.success && j.headline) setHeroSub(j.headline);
    }).catch(function () {});
    loadInsights(identity);
    greetTimer = setTimeout(function () { greetTimer = null; if (isCurrentIdentity(identity)) greet(); }, 2500); /* fallback if the profile is slow */
  }
  window.addEventListener('mcduffy:login', init);
  window.addEventListener('mcduffy:logout', resetVirtualCare);
  init();
})();
